import React from 'react';

// Placeholder blocks shown while content loads. Each skeleton mirrors the
// layout of the real component, so nothing jumps when data arrives.
// Styling lives in `.skeleton` (index.css): a soft block with a light sweep.

export const Bone = ({ className = '' }) => <div className={`skeleton ${className}`} aria-hidden="true" />;

// Vary text-line widths so a list doesn't look like a stamped pattern
const LINE_WIDTHS = [['w-full', 'w-11/12', 'w-3/5'], ['w-full', 'w-4/5'], ['w-5/6'], ['w-full', 'w-full', 'w-2/5']];

const Loading = ({ label = 'Loading', children, className = '' }) => (
  <div role="status" aria-busy="true" aria-label={label} className={className}>
    {children}
  </div>
);

// Matches PostCard's feed layout: 44px avatar, name + handle, text, optional media, action row
export const PostSkeleton = ({ variant = 0, withMedia = false }) => {
  const lines = LINE_WIDTHS[variant % LINE_WIDTHS.length];
  return (
    <div className="px-4 py-3.5 border-b border-zinc-100 dark:border-zinc-800/80">
      <div className="flex gap-3">
        <Bone className="w-11 h-11 rounded-full flex-shrink-0" />
        <div className="flex-1 min-w-0 pt-0.5">
          <div className="flex items-center gap-2 mb-2.5">
            <Bone className="h-3.5 w-28 rounded-full" />
            <Bone className="h-3 w-20 rounded-full opacity-70" />
          </div>
          <div className="space-y-2">
            {lines.map((w, i) => (
              <Bone key={i} className={`h-3 ${w} rounded-full`} />
            ))}
          </div>
          {withMedia && <Bone className="mt-3 w-full aspect-[16/10] rounded-2xl" />}
          <div className="flex items-center justify-between max-w-[340px] mt-4">
            {[0, 1, 2, 3, 4].map((i) => (
              <Bone key={i} className="w-5 h-5 rounded-full opacity-70" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const PostListSkeleton = ({ count = 4 }) => (
  <Loading label="Loading posts">
    {Array.from({ length: count }, (_, i) => (
      <PostSkeleton key={i} variant={i} withMedia={i % 3 === 1} />
    ))}
  </Loading>
);

// Matches PostDetail's expanded post (larger avatar, bigger text, stats row)
export const PostDetailSkeleton = () => (
  <Loading label="Loading post" className="px-4 py-4">
    <div className="flex items-center gap-3">
      <Bone className="w-12 h-12 rounded-full flex-shrink-0" />
      <div className="space-y-2">
        <Bone className="h-4 w-36 rounded-full" />
        <Bone className="h-3 w-24 rounded-full opacity-70" />
      </div>
    </div>
    <div className="mt-4 space-y-2.5">
      <Bone className="h-4 w-full rounded-full" />
      <Bone className="h-4 w-full rounded-full" />
      <Bone className="h-4 w-2/3 rounded-full" />
    </div>
    <Bone className="mt-4 w-full aspect-[16/10] rounded-2xl" />
    <Bone className="mt-4 h-3 w-40 rounded-full opacity-70" />
    <div className="py-3 my-3 border-y border-zinc-100 dark:border-zinc-800/80 flex items-center justify-between max-w-[340px]">
      {[0, 1, 2, 3, 4].map((i) => (
        <Bone key={i} className="w-6 h-6 rounded-full opacity-70" />
      ))}
    </div>
  </Loading>
);

// Matches Profile: banner, overlapping avatar, name, bio, stats, tabs, then posts
export const ProfileSkeleton = () => (
  <Loading label="Loading profile" className="min-h-screen bg-white dark:bg-black">
    <Bone className="w-full h-44 sm:h-52 rounded-none" />
    <div className="px-4">
      <div className="flex justify-between items-end -mt-14 mb-3">
        <div className="rounded-full border-4 border-white dark:border-black">
          <Bone className="w-24 h-24 sm:w-28 sm:h-28 rounded-full" />
        </div>
        <Bone className="h-9 w-28 rounded-full" />
      </div>
      <Bone className="h-5 w-44 rounded-full" />
      <Bone className="mt-2 h-3.5 w-28 rounded-full opacity-70" />
      <div className="mt-4 space-y-2">
        <Bone className="h-3 w-full rounded-full" />
        <Bone className="h-3 w-3/4 rounded-full" />
      </div>
      <div className="mt-4 flex gap-5">
        <Bone className="h-3.5 w-20 rounded-full" />
        <Bone className="h-3.5 w-20 rounded-full" />
      </div>
    </div>
    <div className="mt-5 px-4 flex gap-6 border-b border-zinc-100 dark:border-zinc-800/80 pb-3">
      {[0, 1, 2, 3].map((i) => (
        <Bone key={i} className="h-3.5 w-14 rounded-full" />
      ))}
    </div>
    <PostListSkeleton count={3} />
  </Loading>
);

// Matches the notification row: type icon column, avatar, text lines
export const NotificationListSkeleton = ({ count = 7 }) => (
  <Loading label="Loading notifications" className="divide-y divide-gray-100 dark:divide-zinc-800">
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className="flex gap-3 px-4 py-3.5">
        <div className="flex-shrink-0 pt-0.5 w-6 flex justify-end">
          <Bone className="w-5 h-5 rounded-full opacity-70" />
        </div>
        <div className="flex-1 min-w-0">
          <Bone className="w-8 h-8 rounded-full" />
          <Bone className={`mt-2.5 h-3 rounded-full ${i % 2 ? 'w-3/5' : 'w-4/5'}`} />
          {i % 3 === 0 && <Bone className="mt-2 h-3 w-2/5 rounded-full opacity-70" />}
        </div>
      </div>
    ))}
  </Loading>
);

// Matches the Messages conversation list row
export const ConversationListSkeleton = ({ count = 8 }) => (
  <Loading label="Loading conversations">
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className="flex items-center gap-3 px-4 py-3">
        <Bone className="w-12 h-12 rounded-full flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <Bone className={`h-3.5 rounded-full ${i % 2 ? 'w-24' : 'w-32'}`} />
            <Bone className="h-3 w-8 rounded-full opacity-70" />
          </div>
          <Bone className={`mt-2 h-3 rounded-full ${['w-3/4', 'w-1/2', 'w-2/3'][i % 3]}`} />
        </div>
      </div>
    ))}
  </Loading>
);

// Chat bubbles alternating sides, for an open thread
export const MessageThreadSkeleton = () => {
  const bubbles = [
    ['left', 'w-44'], ['left', 'w-28'], ['right', 'w-52'], ['left', 'w-60'],
    ['right', 'w-32'], ['right', 'w-48'], ['left', 'w-36'],
  ];
  return (
    <Loading label="Loading messages" className="flex flex-col gap-2.5 px-4 py-4">
      {bubbles.map(([side, w], i) => (
        <div key={i} className={`flex ${side === 'right' ? 'justify-end' : 'justify-start'}`}>
          <Bone className={`h-9 ${w} max-w-[75%] rounded-2xl ${side === 'right' ? 'rounded-br-md' : 'rounded-bl-md'}`} />
        </div>
      ))}
    </Loading>
  );
};

// Comment rows under a post: smaller avatar, name, one or two lines
export const CommentListSkeleton = ({ count = 3 }) => (
  <Loading label="Loading comments" className="py-2">
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className="flex gap-2.5 px-4 py-3">
        <Bone className="w-9 h-9 rounded-full flex-shrink-0" />
        <div className="flex-1 min-w-0 pt-0.5">
          <Bone className="h-3 w-24 rounded-full" />
          <Bone className={`mt-2 h-3 rounded-full ${i % 2 ? 'w-1/2' : 'w-4/5'}`} />
          {i === 0 && <Bone className="mt-2 h-3 w-3/5 rounded-full" />}
        </div>
      </div>
    ))}
  </Loading>
);
